import React from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { Box, Grid } from 'transfix-ui/core'
import { IStep1 } from 'apps/fleet-planner/components/activation/types'
import {
  countriesOptions,
  COUNTRIES,
  usStatesOptions,
  canadianStatesOptions,
} from 'constants/countriesStates'
import { carrierEntities } from 'constants/entities'
import { TCompanyInformationSchema } from './CompanyInformationSchema'
import { useCompanyInformationForm } from './usecompanyInformationForm'

const getErrorMessage = <T extends FieldErrors<TCompanyInformationSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface ICompanyInformationFormProps {
  defaultValues?: Partial<TCompanyInformationSchema>
  onSubmitSuccess: SubmitHandler<IStep1>
  onSubmitError?: SubmitErrorHandler<TCompanyInformationSchema>
}

export const CompanyInformationForm = ({
  defaultValues = {},
  onSubmitSuccess,
  onSubmitError = () => {},
}: ICompanyInformationFormProps) => {
  const { control, errors, handleSubmit } = useCompanyInformationForm({
    defaultValues,
  })

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)
  const UsCountryCode = COUNTRIES[0].code
  const currentCountry = useWatch({ name: 'countryCode', defaultValue: UsCountryCode, control })
  const isCountryUS = currentCountry === UsCountryCode
  const stateOptions = isCountryUS ? usStatesOptions : canadianStatesOptions

  return (
    <form onSubmit={submitHandler} data-test='CompanyInformationForm'>
      <Grid item xs={12}>
        <Box mb={2}>
          <ControlledSelect
            control={control}
            name='entityType'
            SelectProps={{
              label: 'Entity Type',
              options: carrierEntities,
              validationMessage: getErrorMessage(errors, 'entityType'),
              error: !!getErrorMessage(errors, 'entityType'),
              large: true,
              placeholder: 'Select an Entity type',
              dataTest: 'input-entity-type',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledTextField
            name='ein'
            control={control}
            TextInputProps={{
              type: 'number',
              label: 'EIN',
              validationMessage: getErrorMessage(errors, 'ein'),
              error: !!getErrorMessage(errors, 'ein'),
              placeholder: 'Enter your EIN number',
              dataTest: 'ein-input',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledSelect
            control={control}
            name='countryCode'
            SelectProps={{
              label: 'Country',
              options: countriesOptions,
              validationMessage: getErrorMessage(errors, 'countryCode'),
              error: !!getErrorMessage(errors, 'countryCode'),
              placeholder: 'Select your Country',
              defaultValue: UsCountryCode,
              dataTest: 'input-country-code',
              required: true,
            }}
          />
        </Box>
        <Box display='flex' flexDirection='column' mb={2} rowGap={8}>
          <ControlledTextField
            name='address'
            control={control}
            TextInputProps={{
              label: 'Address',
              validationMessage: getErrorMessage(errors, 'address'),
              error: !!getErrorMessage(errors, 'address'),
              placeholder: `Enter your company's address`,
              dataTest: 'input-address',
              required: true,
            }}
          />
          <ControlledTextField
            name='addressLine2'
            control={control}
            TextInputProps={{
              validationMessage: getErrorMessage(errors, 'addressLine2'),
              error: !!getErrorMessage(errors, 'addressLine2'),
              placeholder: 'Enter your Apt number (optional)',
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledTextField
            name='city'
            control={control}
            TextInputProps={{
              label: 'City',
              validationMessage: getErrorMessage(errors, 'city'),
              error: !!getErrorMessage(errors, 'city'),
              placeholder: `Enter your city`,
              dataTest: 'city-input',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledSelect
            control={control}
            name='stateCode'
            SelectProps={{
              label: 'State',
              options: stateOptions,
              validationMessage: getErrorMessage(errors, 'stateCode'),
              error: !!getErrorMessage(errors, 'stateCode'),
              placeholder: 'Select a state',
              dataTest: 'state-select',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledTextField
            name='zipcode'
            control={control}
            TextInputProps={{
              label: isCountryUS ? 'ZIP' : 'Postal Code',
              validationMessage: getErrorMessage(errors, 'zipcode'),
              error: !!getErrorMessage(errors, 'zipcode'),
              placeholder: `Enter your Zipcode`,
              dataTest: 'zipcode-input',
              type: isCountryUS ? 'number' : 'text',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledTextField
            name='contactEmail'
            control={control}
            TextInputProps={{
              label: 'Carrier Contact Email',
              validationMessage: getErrorMessage(errors, 'contactEmail'),
              error: !!getErrorMessage(errors, 'contactEmail'),
              placeholder: 'you@yourcompany.com',
              dataTest: 'carrier-contact-email-input',
              className: 'fs-mask',
              required: true,
            }}
          />
        </Box>
        <Box mb={2}>
          <ControlledPhoneInput
            control={control}
            name='contactPhone'
            PhoneInputProps={{
              placeholder: '(555) 123-4567',
              label: 'Carrier Contact Phone',
              validationMessage: getErrorMessage(errors, 'contactPhone'),
              dataTest: 'contact-phone-input',
              className: 'fs-mask',
              required: true,
            }}
          />
        </Box>
        <Grid container justifyContent='flex-end'>
          <Grid item xs={12} md={4}>
            <Button type='submit' color='primary' data-test='submit-button' fullWidth>
              Continue
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}
